/**
 * Defer FareHarbor lightframe API until booking intent.
 * Booking hrefs remain a hard fallback.
 */

const DEFAULT_TIMEOUT_MS = 5000;
const BOOK_LINK_SELECTOR =
  'a[href*="fareharbor.com/embeds/book"], a[href*="demo.fareharbor.com/embeds/book"]';

/** @type {Promise<void> | null} */
let apiLoadPromise = null;
let autoLightframeConfigured = false;

function getConfig() {
  return window.fhsFareHarborDeferral || {};
}

function isEnabled() {
  return Boolean(getConfig().enabled);
}

function getApiSrc() {
  const src = getConfig().apiSrc;
  return typeof src === 'string' && src !== '' ? src : 'https://fareharbor.com/embeds/api/v1/';
}

function getTimeoutMs() {
  const timeout = Number(getConfig().timeoutMs);
  return Number.isFinite(timeout) && timeout > 0 ? timeout : DEFAULT_TIMEOUT_MS;
}

function waitForFh(timeoutMs) {
  return new Promise((resolve, reject) => {
    if (window.FH && typeof window.FH.autoLightframe === 'function') {
      resolve();
      return;
    }

    const started = Date.now();
    const timer = window.setInterval(() => {
      if (window.FH && typeof window.FH.autoLightframe === 'function') {
        window.clearInterval(timer);
        resolve();
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        window.clearInterval(timer);
        reject(new Error('FareHarbor API timed out'));
      }
    }, 50);
  });
}

function ensureApiLoading() {
  if (apiLoadPromise) {
    return apiLoadPromise;
  }

  if (window.FH && typeof window.FH.autoLightframe === 'function') {
    apiLoadPromise = Promise.resolve();
    return apiLoadPromise;
  }

  apiLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-fhs-fh-api="1"]');
    if (existing) {
      waitForFh(getTimeoutMs()).then(resolve).catch(reject);
      return;
    }

    const script = document.createElement('script');
    script.src = getApiSrc();
    script.async = true;
    script.dataset.fhsFhApi = '1';
    script.onload = () => {
      waitForFh(getTimeoutMs()).then(resolve).catch(reject);
    };
    script.onerror = () => {
      apiLoadPromise = null;
      reject(new Error('FareHarbor API failed to load'));
    };
    document.head.appendChild(script);
  });

  return apiLoadPromise;
}

function configureAutoLightframe() {
  if (autoLightframeConfigured) {
    return;
  }
  if (!window.FH || typeof window.FH.autoLightframe !== 'function') {
    return;
  }

  if (typeof window.__fhsFhOnAutoLightframe === 'function') {
    window.__fhsFhOnAutoLightframe();
  } else {
    window.FH.autoLightframe();
  }

  autoLightframeConfigured = true;
}

function setBookingBusy(el, busy) {
  if (!(el instanceof HTMLElement)) {
    return;
  }
  if (busy) {
    el.setAttribute('aria-busy', 'true');
    el.classList.add('fh-booking-loading');
  } else {
    el.removeAttribute('aria-busy');
    el.classList.remove('fh-booking-loading');
  }
}

function openBooking(el) {
  configureAutoLightframe();

  // FH.autoLightframe binds click handlers that parse the anchor href.
  // Re-dispatch so those handlers open the lightframe (FH.open expects options, not a URL).
  if (typeof el.click === 'function') {
    el.click();
    return;
  }

  const href = el.getAttribute('href');
  if (href) {
    window.location.assign(href);
  }
}

function fallbackNavigate(el) {
  const href = el.getAttribute('href');
  if (href) {
    window.location.assign(href);
  }
}

function isBookLink(el) {
  return el instanceof HTMLAnchorElement && el.matches(BOOK_LINK_SELECTOR);
}

function onBookingIntent(event) {
  const el = event.target instanceof Element ? event.target.closest(BOOK_LINK_SELECTOR) : null;
  if (!isBookLink(el)) {
    return;
  }
  ensureApiLoading()
    .then(() => {
      configureAutoLightframe();
    })
    .catch(() => {
      // Intent preload failed; click handler will fall back to href.
    });
}

async function onBookingClick(event) {
  const el = event.target instanceof Element ? event.target.closest(BOOK_LINK_SELECTOR) : null;
  if (!isBookLink(el)) {
    return;
  }

  // Modified clicks should keep native behavior (new tab, etc.).
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    ensureApiLoading().catch(() => {});
    return;
  }

  if (window.FH && typeof window.FH.autoLightframe === 'function') {
    configureAutoLightframe();
    // Let FH's own click handlers run.
    return;
  }

  event.preventDefault();
  setBookingBusy(el, true);

  try {
    await ensureApiLoading();
    openBooking(el);
  } catch (error) {
    fallbackNavigate(el);
  } finally {
    setBookingBusy(el, false);
  }
}

export function initFareHarborIntentDeferral() {
  if (!isEnabled()) {
    return;
  }

  document.addEventListener('pointerdown', onBookingIntent, true);
  document.addEventListener('mouseover', onBookingIntent, true);
  document.addEventListener('focusin', onBookingIntent, true);
  document.addEventListener('click', onBookingClick, true);
}
